import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Group, Skeleton, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { APP_PREVIEW_PLATFORM, APP_PREVIEW_QUERY_KEYS, HOME_ADAPTS_HELP } from '@/lib/constants/appPreview';
import type { AppCfgsDto } from '@/lib/types/App';
import { AppPreviewUtils } from '../AppPreviewUtils';
import { useAppPreviewBrowse, useAppPreviewCfg } from '../useAppPreview';
import { AppTvFrame } from './AppTvFrame';

interface AppTvPreviewProps {
  app: AppCfgsDto;
  /** The `?p=` the tab's shell sends (roku, firetv); the frame's layout is the shared one. */
  platform?: string;
}

/**
 * In-browser TV frame rendering the app's LIVE /app/v1 cfg + browse — the same
 * payloads the TV shells load (WS-2.3 app preview, transport per D-2.6).
 * It mirrors the shared home screen (design: ott/design system/surfaces.md
 * "Home"): NavBar strip, hero billboard in the app's hero style (design
 * ADR-0016: card, immersive, full_bleed on the raw poster, no blur), content
 * rails, in the layout the server chose for the catalog (design ADR-0015:
 * rails, one screen, billboard, empty), the one-item title page, and the
 * app's Home image as uploaded when it is on — real theme, real rows, real
 * artwork; hovering simulates remote focus; scrolling is the content-bounded
 * slide.
 * Mounted on the Roku and (3.34.0) Fire TV tabs as the common TV preview: the
 * layout is the contract's for both shells; device details differ and are not
 * modelled.
 */
export function AppTvPreview({ app, platform = APP_PREVIEW_PLATFORM }: AppTvPreviewProps) {
  const queryClient = useQueryClient();
  const appDomain = AppPreviewUtils.appDomain(app);
  const cfgQuery = useAppPreviewCfg(appDomain, platform);
  const browseQuery = useAppPreviewBrowse(appDomain, platform);

  if (!appDomain) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        The preview needs your app&apos;s domain. Set one up on the Web tab first.
      </Alert>
    );
  }

  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: APP_PREVIEW_QUERY_KEYS.all });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Text fw={600} size="lg">
            App Preview
          </Text>
          <Text size="sm" c="dimmed">
            Rendered from your app&apos;s live configuration — the same data your TV app
            loads. The home screen layout is shared by your Fire TV and Roku apps; device
            details such as transitions and the player differ. Recent changes can take a
            couple of minutes to appear.
          </Text>
        </div>
        <Button
          variant="default"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          onClick={handleRefresh}
          loading={cfgQuery.isFetching || browseQuery.isFetching}
        >
          Refresh
        </Button>
      </Group>

      {cfgQuery.isError || browseQuery.isError ? (
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          The preview could not load your app&apos;s configuration. If the app or its
          domain was just enabled, give it a couple of minutes and refresh. The app must
          be Active for its content service to answer.
        </Alert>
      ) : cfgQuery.data && browseQuery.data ? (
        <>
          <AppTvFrame cfg={cfgQuery.data} browse={browseQuery.data} />
          <Text size="sm" c="dimmed" maw={960}>
            {HOME_ADAPTS_HELP}
          </Text>
        </>
      ) : (
        <Skeleton radius="lg" style={{ aspectRatio: '16 / 9', width: '100%' }} />
      )}
    </Stack>
  );
}
